import {
    AdvancedMarker,
    APIProvider,
    Map,
    useMap,
    useMapsLibrary,
} from "@vis.gl/react-google-maps";
import {useEffect} from "react";
import {useQuery} from "@tanstack/react-query";
import MapDarkOverlay from "../../../../../../../../components/map-dark-overlay/map-dark-overlay.jsx";
import corporationService from "../../../../../../../../services/web/corporation-service.js";

const DEFAULT_CENTER = {lat: -23.0, lng: -46.0};

function LocationPointsMap({locations}) {
    const map = useMap();
    const coreLib = useMapsLibrary("core");
    const activeIndex = locations.reduce((highestIndex, location, index) => {
        if (highestIndex === -1 || location.qty > locations[highestIndex].qty) return index;

        return highestIndex;
    }, -1);

    useEffect(() => {
        if (!map || !coreLib || locations.length === 0) return;

        if (locations.length === 1) {
            map.setCenter({lat: locations[0].latitude, lng: locations[0].longitude});
            map.setZoom(14);
            return;
        }

        const bounds = new coreLib.LatLngBounds();

        locations.forEach(location => {
            bounds.extend({lat: location.latitude, lng: location.longitude});
        });
        map.fitBounds(bounds, 36);
    }, [coreLib, locations, map]);

    return (
        <Map
            reuseMaps
            mapId="SOS_ACTIVATIONS_MAP_ID"
            defaultZoom={12}
            defaultCenter={locations[0]
                ? {lat: locations[0].latitude, lng: locations[0].longitude}
                : DEFAULT_CENTER}
            disableDefaultUI
            keyboardShortcuts={false}
            streetViewControl={false}
            mapTypeControl={false}
            fullscreenControl={false}
            zoomControl={false}
            cameraControl={false}
            gestureHandling="cooperative"
            style={{height: "100%", width: "100%"}}
        >
            <MapDarkOverlay />
            {locations.map((location, index) => {
                const isActive = index === activeIndex;
                const markerColor = isActive ? "#00d3cf" : "#004687";

                return (
                    <AdvancedMarker
                        key={`${location.latitude}-${location.longitude}-${index}`}
                        position={{lat: location.latitude, lng: location.longitude}}
                        title={`Local do acionamento: ${location.qty}`}
                        zIndex={isActive ? 100 : 1}
                    >
                        <div
                            className={`relative flex h-7 w-7 -rotate-45 items-center justify-center rounded-[50%_50%_50%_0] transition-transform duration-200 ${isActive ? "scale-110 shadow-[0_5px_12px_rgba(0,0,0,0.45)]" : "hover:scale-105"}`}
                            style={{backgroundColor: markerColor}}
                        >
                            <span className={`h-2.5 w-2.5 rounded-full bg-[#666A6B] ${isActive ? "shadow-inner" : ""}`} />
                        </div>
                    </AdvancedMarker>
                );
            })}
        </Map>
    );
}

export default function LocationPoints({data}) {
    const locations = (data?.alarms?.locations ?? [])
        .filter(location =>
            Number.isFinite(Number(location.latitude)) &&
            Number.isFinite(Number(location.longitude))
        )
        .map(location => ({
            ...location,
            latitude: Number(location.latitude),
            longitude: Number(location.longitude),
            qty: Number(location.qty) || 0,
        }));
    const {data: corporation} = useQuery({
        queryKey: ["corporation"],
        queryFn: () => corporationService.entity(),
    });

    return (
        <article className="col-span-4 box-border flex h-full min-h-0 min-w-0 w-full flex-col overflow-hidden">
            <h3 className="mb-1.5! shrink-0 text-center text-(length:--font-md)! text-[#AAAAAA]">
                Local dos acionamentos
            </h3>
            <div className="flex-1 min-h-0 overflow-hidden rounded-xl">
                {!corporation?.maps_api_key && (
                    <div className="flex h-full items-center justify-center p-4 text-center text-sm text-[#AAAAAA]">
                        Chave do Google Maps não configurada.
                    </div>
                )}

                {corporation?.maps_api_key && (
                    <APIProvider apiKey={corporation.maps_api_key} language="pt-BR">
                        <LocationPointsMap locations={locations} />
                    </APIProvider>
                )}
            </div>
        </article>
    );
}
